import { useState, useEffect } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import apiService from '../apiService';

const CmsPageScreen = ({ slug, pageTitle, onBack, setActiveTab, appTheme = 'blue', appSettings }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const goHome = () => setActiveTab && setActiveTab('home');

  useEffect(() => {
    if (!slug) {
      setError('No page specified.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    apiService.get(`/cms-pages/${slug}`)
      .then(data => {
        setPage(data);
      })
      .catch(() => {
        setError('Page not found.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--header-gradient)', color: 'var(--text-light)' };

  const title = page?.title || pageTitle || 'Page';

  return (
    <div className="pb-20 min-h-screen" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      {/* HEADER */}
      <div className="p-6 rounded-b-3xl shadow-lg" style={headerStyle}>
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={onBack || goHome}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
          >
            <ArrowLeft size={22} />
          </button>
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
      </div>

      {/* CONTENT */}
      <div className="p-4">
        {loading && (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText size={48} className="text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">{error}</p>
            <button
              onClick={onBack || goHome}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium"
              style={{ background: 'var(--header-gradient)', color: 'var(--text-light)' }}
            >
              Go Home
            </button>
          </div>
        )}

        {!loading && page && (
          <>
            <style>{`
              .cms-page-content { color: #374151; font-size: 15px; line-height: 1.75; }
              .cms-page-content h1 { font-size: 1.6em; font-weight: 700; margin: 1em 0 .4em; color: #111827; }
              .cms-page-content h2 { font-size: 1.35em; font-weight: 700; margin: 1em 0 .4em; color: #111827; }
              .cms-page-content h3 { font-size: 1.15em; font-weight: 600; margin: .8em 0 .3em; color: #1f2937; }
              .cms-page-content p  { margin: .5em 0; }
              .cms-page-content ul { list-style: disc;    padding-left: 1.5em; margin: .5em 0; }
              .cms-page-content ol { list-style: decimal; padding-left: 1.5em; margin: .5em 0; }
              .cms-page-content li { margin: .25em 0; }
              .cms-page-content a  { color: #2563eb; text-decoration: underline; }
              .cms-page-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.2em 0; }
              .cms-page-content img { max-width: 100%; height: auto; border-radius: 8px; margin: .5em 0; }
              .cms-page-content blockquote { border-left: 3px solid #d1d5db; padding-left: .75em; color: #6b7280; margin: .6em 0; font-style: italic; }
              .cms-page-content strong, .cms-page-content b { font-weight: 700; color: #111827; }
              .cms-page-content table { width: 100%; border-collapse: collapse; margin: .5em 0; font-size: .9em; }
              .cms-page-content th, .cms-page-content td { border: 1px solid #e5e7eb; padding: .4em .6em; text-align: left; }
              .cms-page-content th { background: #f9fafb; font-weight: 600; }
            `}</style>
            <div
              className="cms-page-content bg-white rounded-xl shadow-sm p-4"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CmsPageScreen;
